import { useEffect } from 'react';
import { Select } from '@/components/ui/Select';
import { useSelectLabels } from '@/hooks/useSelectLabels';
import { useActiveStores } from '@/hooks/useActiveStores';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { setSelectedStore } from '@/store/appSlice';
import { notify } from '@/core/toast/notify';
import { clearPosSessionStorage } from '@/utils/posSessionStorage';

interface PosStoreSelectorProps {
  /** Hide the field label — for compact headers */
  compact?: boolean;
  className?: string;
}

export function PosStoreSelector({ compact = true, className }: PosStoreSelectorProps) {
  const selectLabels = useSelectLabels();
  const dispatch = useAppDispatch();
  const selected = useAppSelector((s) => s.app.selectedStore);
  const selectedCompany = useAppSelector((s) => s.app.selectedCompany);
  const { stores, loading } = useActiveStores();

  const visibleStores = selectedCompany
    ? stores.filter((s) => s.companyId === selectedCompany.id || s.companyId == null)
    : stores;

  useEffect(() => {
    if (selected || loading || visibleStores.length !== 1) return;
    dispatch(setSelectedStore(visibleStores[0]));
  }, [dispatch, loading, selected, visibleStores]);

  useEffect(() => {
    if (!selected || loading || visibleStores.length === 0) return;
    if (!visibleStores.some((s) => s.id === selected.id)) {
      clearPosSessionStorage();
      dispatch(setSelectedStore(null));
    }
  }, [dispatch, loading, selected, visibleStores]);

  const onChange = (value: string) => {
    const store = visibleStores.find((s) => String(s.id) === value);
    if (!store || store.id === selected?.id) return;
    clearPosSessionStorage();
    dispatch(setSelectedStore(store));
    notify.t.success('toast.workingInStore', { store: store.name, ns: 'pos' });
  };

  return (
    <Select
      label={compact ? undefined : 'Store'}
      className={className ?? 'min-w-[9rem] max-w-[15rem]'}
      headerOverlay
      placeholder={loading ? selectLabels.loadingStores : selectLabels.selectStore}
      disabled={loading || visibleStores.length === 0}
      value={selected ? String(selected.id) : ''}
      options={visibleStores.map((s) => ({
        value: String(s.id),
        label: `${s.name} (${s.code})`,
      }))}
      onChange={onChange}
      allowClear={false}
    />
  );
}
